import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { commissionChartAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Percent, Package, Info } from 'lucide-react';
import { useAuth } from '../lib/auth-context';

const CommissionChart = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ package: null, commissions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await commissionChartAPI.getMy();
        setData(r.data || { package: null, commissions: [] });
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, []);

  const userRole = user?.role;
  const getCommField = () => {
    if (userRole === 'retailer') return 'retailer_commission';
    if (userRole === 'distributor') return 'distributor_commission';
    if (userRole === 'super_distributor') return 'super_distributor_commission';
    return 'retailer_commission';
  };
  const commField = getCommField();
  const roleLabel = userRole?.replace('_', ' ') || 'User';

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="commission-chart-page">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2 text-slate-900">
            <Percent className="w-5 h-5" style={{ color: '#4680ff' }} />
            Commission Chart
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Your commission rates for all operators</p>
        </div>

        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : !data.package ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Info className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500 font-medium">No commission package assigned</p>
              <p className="text-xs text-slate-400 mt-1">Please contact your admin to assign a commission package.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Package Info */}
            <Card className="border-blue-100 bg-blue-50/30">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#4680ff]/10 flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#4680ff]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">{data.package.name}</p>
                  <p className="text-xs text-slate-500 capitalize">Package - {roleLabel}</p>
                </div>
              </CardContent>
            </Card>

            {/* Commission Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/50">
                        <TableHead className="text-[11px] font-semibold text-slate-500 uppercase pl-4">Operator</TableHead>
                        <TableHead className="text-[11px] font-semibold text-slate-500 uppercase">Service</TableHead>
                        <TableHead className="text-[11px] font-semibold text-slate-500 uppercase text-center">Commission Type</TableHead>
                        <TableHead className="text-[11px] font-semibold text-slate-500 uppercase text-center">Your Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.commissions.length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-slate-400">No commission rates configured</TableCell></TableRow>
                      ) : data.commissions.map((c, idx) => (
                        <TableRow key={c.id || idx} className="border-b border-slate-50">
                          <TableCell className="pl-4 font-semibold capitalize text-sm text-slate-800">{c.operator?.replace(/_/g, ' ')}</TableCell>
                          <TableCell><Badge variant="outline" className="capitalize text-[10px]">{c.service}</Badge></TableCell>
                          <TableCell className="text-center">
                            <Badge className={`text-[10px] ${c.commission_type === 'percentage' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                              {c.commission_type === 'percentage' ? 'Percentage' : 'Flat'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-sm font-bold text-green-700">
                              {c[commField] || 0}{c.commission_type === 'percentage' ? '%' : ' Rs'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CommissionChart;
